import React, { useState, useRef, useEffect } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import { useForm } from 'react-hook-form';
import Modal from '@/components/modal/Modal';
import { RegularText, MediumText, DemiLightText } from '@/lib/Text';
import SvgIcon from '@/components/SvgIcon';
import { useCreateCenterNotice, useUpdateCenterNotice, useNoticePresignedUrls } from '@/features/centerNotice';
import { CenterNotice, TargetMemberStatus } from '@/features/centerNotice/types';
import { Col, Row } from '@/lib/utils';
import axios from 'axios';
import { overlay } from 'overlay-kit';
import ModalNoticePreview from './components/ModalNoticePreview';
import ModalNoticeConfirm from './components/ModalNoticeConfirm';
import SchedulePicker from './components/SchedulePicker';
import ActionButton from './components/ActionButton';
import { colors } from '@/lib/colors';

dayjs.locale('ko');

type Props = {
    onClose?: () => void;
    centerId: string;
    mode?: 'create' | 'duplicate' | 'edit';
    initialNotice?: CenterNotice;
    onCreated?: (noticeId: string) => void;
};

type FormValues = {
    title: string;
    content: string;
    targetMemberStatus: TargetMemberStatus;
    imageUrls: string[];
};

const TARGET_OPTIONS: { value: TargetMemberStatus; label: string }[] = [
    { value: 'all', label: '전체 회원' },
    { value: 'active', label: '수강권 활성화된 모든 회원' },
    { value: 'expired', label: '수강권 만료된 모든 회원' },
];

const MODE_CONFIG = {
    create: { title: '공지 사항 등록', button: '등록하기' },
    duplicate: { title: '공지 사항 등록', button: '등록하기' },
    edit: { title: '공지 사항 수정', button: '수정하기' },
};

const ModalCreateNotice = ({ onClose, centerId, mode = 'create', initialNotice, onCreated }: Props) => {
    const { control, handleSubmit, watch, setValue } = useForm<FormValues>({
        defaultValues: {
            title: initialNotice?.title ?? '',
            content: initialNotice?.content ?? '',
            targetMemberStatus: initialNotice?.targetMemberStatus ?? 'all',
            imageUrls: initialNotice?.imageUrls ?? [],
        },
    });

    const [isUploading, setIsUploading] = useState(false);
    const [isTargetDropdownOpen, setIsTargetDropdownOpen] = useState(false);
    const [publishAt, setPublishAt] = useState<string | null>(initialNotice?.publishAt ?? null);
    const [isScheduleOpen, setIsScheduleOpen] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const targetDropdownRef = useRef<HTMLDivElement>(null);

    const title = watch('title');
    const content = watch('content');
    const imageUrls = watch('imageUrls');
    const targetMemberStatus = watch('targetMemberStatus');

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (targetDropdownRef.current && !targetDropdownRef.current.contains(event.target as Node)) {
                setIsTargetDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const createNotice = useCreateCenterNotice();
    const updateNotice = useUpdateCenterNotice();
    const getPresignedUrls = useNoticePresignedUrls();

    const executeCreate = (data: FormValues) => {
        createNotice.mutate(
            {
                centerId,
                body: {
                    title: data.title,
                    content: data.content,
                    targetMemberStatus: data.targetMemberStatus,
                    imageUrls: data.imageUrls,
                    publishAt,
                },
            },
            {
                onSuccess: (res) => {
                    if (res?.data?.id) {
                        onCreated?.(res.data.id);
                    }
                    onClose && onClose();
                },
                onError: (err) => {
                    console.error(err);
                    alert('공지사항 등록에 실패했습니다.');
                },
            },
        );
    };

    const executeUpdate = (data: FormValues) => {
        if (!initialNotice) return;
        updateNotice.mutate(
            {
                centerId,
                noticeId: initialNotice.id,
                body: {
                    title: data.title,
                    content: data.content,
                    imageUrls: data.imageUrls,
                    publishAt,
                },
            },
            {
                onSuccess: () => {
                    onClose && onClose();
                },
                onError: (err) => {
                    console.error(err);
                    alert('공지사항 수정에 실패했습니다.');
                },
            },
        );
    };

    const targetLabel = TARGET_OPTIONS.find((o) => o.value === targetMemberStatus)?.label ?? '전체 회원';

    const onSubmit = (data: FormValues) => {
        const isEdit = mode === 'edit' && initialNotice;

        overlay.open(({ unmount: unmountConfirm }) => (
            <ModalNoticeConfirm
                isEdit={!!isEdit}
                publishAt={publishAt}
                targetLabel={targetLabel}
                onCancel={unmountConfirm}
                onConfirm={() => {
                    unmountConfirm();
                    if (isEdit) {
                        executeUpdate(data);
                    } else {
                        executeCreate(data);
                    }
                }}
            />
        ));
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        if (imageUrls.length + files.length > 3) {
            alert('이미지는 최대 3장까지 등록 가능합니다.');
            return;
        }

        setIsUploading(true);
        try {
            const mediaContents = Array.from(files).map((file) => ({
                type: 'image' as const,
                extension: file.name.split('.').pop() || 'jpg',
                fileName: file.name,
                content: '', // Presigned URL 요청 시 content는 필요 없음
                key: `notice/${Date.now()}_${file.name}`,
            }));

            const presignedData = await getPresignedUrls.mutateAsync({
                centerId,
                mediaContents: mediaContents.map((m) => ({ ...m, content: '' })),
            });

            if (!presignedData?.data) {
                throw new Error('Failed to get presigned URLs');
            }

            const newImageUrls: string[] = [];

            // Upload files to S3
            await Promise.all(
                presignedData.data.map(async (presigned, index) => {
                    const file = files[index];
                    await axios.put(presigned.url, file, {
                        headers: {
                            'Content-Type': file.type,
                        },
                    });
                    const viewUrl = presigned.url.split('?')[0];
                    newImageUrls.push(viewUrl);
                }),
            );

            setValue('imageUrls', [...imageUrls, ...newImageUrls]);
        } catch (error) {
            console.error(error);
            alert('이미지 업로드에 실패했습니다.');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const removeImage = (index: number) => {
        const newUrls = [...imageUrls];
        newUrls.splice(index, 1);
        setValue('imageUrls', newUrls);
    };

    return (
        <Modal
            onClose={onClose}
            whiteViewWidth={640} // 디자인에 맞게 조절
            borderRadius={37}
            isCloseButton={false} // 커스텀 헤더 사용
            mt={91}
            mb={150}
            isWhiteOverFlowScroll={false}
        >
            <Col className="w-full h-full relative">
                {/* Header */}
                <Row className="justify-between items-center px-[30px] pt-[30px] pb-[33px]">
                    <MediumText fontSize={25} lineHeight={34} color={colors.Text_1} letterSpacing={-0.6}>
                        {MODE_CONFIG[mode].title}
                    </MediumText>
                    <Row className="items-center gap-[10px]">
                        <ActionButton
                            iconName="SvgEye"
                            label="미리 보기"
                            onClick={() => {
                                overlay.open(({ unmount }) => (
                                    <ModalNoticePreview
                                        title={title}
                                        content={content}
                                        imageUrls={imageUrls}
                                        onClose={unmount}
                                        onEdit={unmount}
                                        onSubmit={() => {
                                            unmount();
                                            handleSubmit(onSubmit)();
                                        }}
                                    />
                                ));
                            }}
                            strokeWidth={1}
                            size={20}
                            fontSize={16}
                            className="bg-[#F2F4F6] border-[#E6E8EA] px-[8px] gap-[1px]"
                        />
                        {(mode !== 'edit' || initialNotice?.status === 'scheduled') && (
                            <div className="relative">
                                {publishAt ? (
                                    <div className="px-[14px] py-[7px] bg-primary/10 rounded-[9px] flex items-center gap-[8px]">
                                        <SvgIcon name="SvgClock" size={16} fill={colors.primary} />
                                        <DemiLightText fontSize={15} color={colors.primary} letterSpacing={-0.5}>
                                            {dayjs(publishAt).format('YYYY.MM.DD (dd) HH:mm')} 예약
                                        </DemiLightText>
                                        <div
                                            className="cursor-pointer ml-[2px] rounded-full bg-[#70777E] w-[15px] h-[15px] flex items-center justify-center"
                                            onClick={() => setPublishAt(null)}
                                        >
                                            <SvgIcon name="SvgX" size={14} stroke={'white'} strokeWidth={1} />
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <ActionButton
                                            iconName="SvgClock"
                                            label="예약 등록"
                                            onClick={() => setIsScheduleOpen(!isScheduleOpen)}
                                            isFill
                                            size={18}
                                            fontSize={16}
                                            className="bg-[#F2F4F6] border-[#E6E8EA] px-[8px] gap-[1px]"
                                        />
                                        {isScheduleOpen && (
                                            <SchedulePicker
                                                initialDate={publishAt}
                                                onClose={() => setIsScheduleOpen(false)}
                                                onConfirm={(dateStr) => {
                                                    setPublishAt(dateStr);
                                                    setIsScheduleOpen(false);
                                                }}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                        <div
                            className="cursor-pointer ml-[10px]"
                            onClick={() => {
                                onClose && onClose();
                            }}
                        >
                            <SvgIcon name="SvgX" size={32} stroke="#8C919B" />
                        </div>
                    </Row>
                </Row>

                {/* Content Scroll Area */}
                <Col className="flex pb-[41px]  px-[30px] ">
                    {/* Title Input */}
                    <div className="relative w-full mb-[6px]">
                        <input
                            type="text"
                            placeholder="제목을 입력해주세요."
                            className="w-full h-[54px] bg-trans border border-solid border-[#E6E8EA] rounded-[12px] px-[20px] transition-all placeholder:text-[#C6C8C9] font-noto-regular text-[17px] leading-[26px] hover:border-primary focus:border-primary outline-none"
                            maxLength={65}
                            style={{
                                letterSpacing: '-0.7px',
                            }}
                            {...control.register('title')}
                        />
                    </div>
                    <div className="flex w-full  justify-end mb-[22px]">
                        <RegularText fontSize={13} color={colors.Text_1} letterSpacing={-0.4}>
                            {title.length}
                        </RegularText>
                        <RegularText fontSize={13} color="#848F9A" letterSpacing={-0.4}>
                            /65자
                        </RegularText>
                    </div>
                    {/* Content Textarea */}
                    <div className="relative w-full mb-[6px]">
                        <textarea
                            placeholder="내용을 입력해주세요."
                            className="w-full h-[348px] bg-trans border border-solid border-[#E6E8EA] rounded-[20px] p-[20px] text-[16px] leading-[30px] outline-none transition-all resize-none placeholder:text-[#C6C8C9] hover:border-primary focus:border-primary text-GREY_600 font-noto-demilight"
                            maxLength={500}
                            style={{
                                letterSpacing: '-0.7px',
                            }}
                            {...control.register('content')}
                        />
                    </div>
                    <div className="flex w-full  justify-end mb-[34px]">
                        <RegularText fontSize={13} color={colors.Text_1}>
                            {content.length}
                        </RegularText>
                        <RegularText fontSize={13} color="#848F9A">
                            /500자
                        </RegularText>
                    </div>
                    {/* Image Upload */}
                    <Row className="gap-[13px] mb-[20px]">
                        {/* Upload Button */}
                        {imageUrls.length < 3 && (
                            <div
                                className="w-[104px] h-[104px] bg-[#F6F8FA] rounded-[12px] flex flex-col items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors relative border-[0.5px] border-solid border-[#E6E8EA]"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    className="hidden"
                                    accept="image/jpg,image/jpeg,image/png"
                                    multiple
                                    onChange={handleImageUpload}
                                />
                                <SvgIcon name="SvgPlus" size={24} fill="#808B96" className="mb-[9px]" />
                                <MediumText fontSize={15} lineHeight={20} color="#808B96">
                                    이미지
                                </MediumText>
                                {isUploading && (
                                    <div className="absolute inset-0 bg-black/10 flex items-center justify-center rounded-[12px]">
                                        ...
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Image Previews */}
                        {imageUrls.map((url, idx) => (
                            <div
                                key={idx}
                                className="w-[104px] h-[104px] relative rounded-[12px] overflow-hidden group"
                            >
                                <img src={url} alt={`upload-${idx}`} className="w-full h-full object-cover" />
                                <div
                                    className="absolute top-[4px] right-[4px] w-[20px] h-[20px] bg-white rounded-full flex items-center justify-center cursor-pointer  border-[0.5px] border-[#E8E8E8] border-solid"
                                    onClick={() => removeImage(idx)}
                                >
                                    <SvgIcon name="SvgX" size={12} stroke="#79828C" strokeWidth={2} />
                                </div>
                            </div>
                        ))}
                    </Row>
                    <Col className={`mb-[${mode === 'edit' ? '0' : '30px'}] gap-[4px]`}>
                        <DemiLightText fontSize={15} lineHeight={24} color="#848F9A" letterSpacing={-0.4}>
                            • 이미지는 <span className="text-primary font-noto-regular">최대 3장</span>까지 등록
                            가능합니다.
                        </DemiLightText>
                        <DemiLightText fontSize={15} lineHeight={24} color="#848F9A" letterSpacing={-0.4}>
                            • 10mb 이하의 jpg, jpeg, png 이미지만 등록됩니다.
                        </DemiLightText>
                    </Col>
                    {/* Target Select */}
                    {mode !== 'edit' && (
                        <div className="relative w-full " ref={targetDropdownRef}>
                            <div
                                className={`w-full h-[54px] bg-white border border-solid rounded-[12px] px-[20px] flex items-center justify-between cursor-pointer transition-all hover:border-primary ${
                                    isTargetDropdownOpen ? 'border-primary' : 'border-[#E6E8EA]'
                                }`}
                                onClick={() => setIsTargetDropdownOpen(!isTargetDropdownOpen)}
                            >
                                <RegularText fontSize={16} lineHeight={24} color="#848F9A">
                                    {TARGET_OPTIONS.find((o) => o.value === targetMemberStatus)?.label ?? '전체 회원'}
                                </RegularText>
                                <SvgIcon
                                    name="SvgDownStrokeArrow"
                                    size={16}
                                    fill={colors.trans}
                                    stroke="#848F9A"
                                    strokeWidth={1.5}
                                    className={`transition-transform ${isTargetDropdownOpen ? 'rotate-180' : ''}`}
                                />
                            </div>
                            {isTargetDropdownOpen && (
                                <div className="flex flex-col gap-[8px] pt-[10px] absolute w-[260px] top-[58px] left-0  bg-white border border-solid border-[#E3E6E9] rounded-[12px] py-[8px]  shadow-[0px_4px_12px_0px_rgba(0,0,0,0.08)] z-[300]">
                                    {TARGET_OPTIONS.map((option) => (
                                        <div
                                            key={option.value}
                                            className={`px-[8px] py-[12px] cursor-pointer transition-colors mx-[12px] rounded-[15px] ${
                                                option.value === targetMemberStatus
                                                    ? 'bg-[#F2F4F6]'
                                                    : 'hover:bg-[#F6F8FA]'
                                            }`}
                                            onClick={() => {
                                                setValue('targetMemberStatus', option.value);
                                                setIsTargetDropdownOpen(false);
                                            }}
                                        >
                                            <RegularText
                                                fontSize={17}
                                                lineHeight={17}
                                                letterSpacing={-0.6}
                                                color={option.value === targetMemberStatus ? '#3182F6' : colors.Text_1}
                                            >
                                                {option.label}
                                            </RegularText>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </Col>

                {/* Footer Button (Fixed) */}
                <div className="w-full px-[32px] pb-[30px]">
                    <button
                        className={`w-full h-[62px] rounded-[14px] flex items-center justify-center transition-colors ${
                            title.trim() && content.trim()
                                ? 'bg-primary hover:bg-blue-600 cursor-pointer'
                                : 'bg-[#D1D5D9] cursor-not-allowed'
                        }`}
                        onClick={title.trim() && content.trim() ? handleSubmit(onSubmit) : undefined}
                        disabled={!title.trim() || !content.trim()}
                    >
                        <RegularText fontSize={20} lineHeight={24} color="white">
                            {MODE_CONFIG[mode].button}
                        </RegularText>
                    </button>
                </div>
            </Col>
        </Modal>
    );
};

export default ModalCreateNotice;
